import { useState } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faPlus,
  faEye,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import AddShop from "./shop/AddShop";
import EditShop from "./shop/EditShop";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import { useAuth, useShops } from "../../hooks";

const ManageShopO = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const {
    shops,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    registerShop,
    updateShop,
    refresh,
  } = useShops();

  const canView = hasPermission([
    "manage_shop_modules",
    "manage_shop",
    "read_shop",
  ]);
  const canCreate = hasPermission([
    "manage_shop_modules",
    "manage_shop",
    "create_shop",
  ]);
  const canUpdate = hasPermission([
    "manage_shop_modules",
    "manage_shop",
    "update_shop",
  ]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);

  const safeShops = Array.isArray(shops) ? shops : [];
  const totalItems = pagination?.total ?? safeShops.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  const handleAddShop = async (shopData) => {
    await registerShop(shopData);
    setIsAddModalOpen(false);
  };

  const handleUpdateShop = async (shopData) => {
    const id = shopData?.shopId || shopData?.id;
    if (id) {
      await updateShop(id, shopData);
    }
    setIsEditModalOpen(false);
    setSelectedShop(null);
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faStore}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage shops
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Manage all your shop locations
              </p>
            </div>
          </div>
          {canCreate && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add shop
            </Button>
          )}
        </div>
      </Card>

      <Card className="bg-white border border-gray-200 p-3 sm:p-4 lg:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <SearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Search by name, location, type..."
            className="flex-1"
          />
        </div>
      </Card>

      <TableWrapper
        title="shops"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                #
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Shop name
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Location
              </th>
              <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                Type
              </th>
              <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && safeShops.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-6 sm:py-8 text-sm text-gray-500"
                >
                  Loading shops...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-6 sm:py-8 text-sm text-red-500"
                >
                  Failed to load shops.
                  <Button
                    onClick={() => refresh()}
                    className="ml-3 bg-blue-800 hover:bg-blue-900 text-white"
                  >
                    Retry
                  </Button>
                </td>
              </tr>
            ) : safeShops.length > 0 ? (
              safeShops.map((shop, i) => {
                const globalIndex = startIndex + i + 1;
                const shopId = shop.shopId ?? shop.id;
                return (
                  <tr
                    key={shopId}
                    className="hover:bg-blue-50/50 transition-colors"
                  >
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-600 font-medium">
                      {globalIndex}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-800 text-sm">
                      {shop.shop_name}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden md:table-cell truncate max-w-[120px]">
                      {shop.location}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden lg:table-cell">
                      {shop.shop_type}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4">
                      <div className="flex gap-1 sm:gap-2 justify-center">
                        {canView && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() =>
                              navigate(`/shop/shopDetails/${shopId}`)
                            }
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                        )}
                        {canUpdate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => {
                              setSelectedShop(shop);
                              setIsEditModalOpen(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-6 sm:py-8">
                  <EmptyState
                    title="No shops found"
                    message={
                      searchTerm
                        ? "Try adjusting your search or filters"
                        : "Add your first shop to get started"
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canCreate && isAddModalOpen && (
        <AddShop
          onAdd={handleAddShop}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      {canUpdate && isEditModalOpen && selectedShop && (
        <EditShop
          shop={selectedShop}
          onUpdate={handleUpdateShop}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedShop(null);
          }}
        />
      )}
    </div>
  );
};

export default ManageShopO;
